import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EditorContent } from '@tiptap/react';
import { TrashIcon } from '@/features/notes/icons';
import AssetUploadModal from '@/features/notes/AssetUploadModal';
import NoteSharesSection from '@/features/notes/NoteSharesSection';
import NoteTagsSection from '@/features/notes/NoteTagsSection';
import StatPill from '@/components/ui/StatPill';
import { assetsApi } from '@/api/assets';
import ImageBubbleMenu from '@/features/notes/ImageBubbleMenu';
import TableBubbleMenu from '@/features/notes/TableBubbleMenu';
import { clearDraft } from '@/features/notes/noteDraft';
import { formatRelative } from '@/lib/format';
import { notesApi, type NoteDetail } from '@/api/notes';
import { errorMessage } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { AutoSaveIndicator } from '@/features/notes/editor/AutoSaveIndicator';
import { NoteDeleteConfirm } from '@/features/notes/editor/NoteDeleteConfirm';
import { VersionsSection } from '@/features/notes/editor/NoteVersionsSection';
import { TemplateToggleButton } from '@/features/notes/editor/TemplateToggleButton';
import { loadNoteContent } from '@/features/notes/editor/loadNoteContent';
import { encryptAndSaveNote } from '@/features/notes/editor/saveNote';
import { useAssetWrapPropagation } from '@/features/notes/editor/useAssetWrapPropagation';
import { useNoteDelete } from '@/features/notes/editor/useNoteDelete';
import { useNoteDraft, useNoteDraftStatus } from '@/features/notes/editor/useNoteDraft';
import { useNoteTiptap } from '@/features/notes/editor/useNoteTiptap';

interface Props {
  tenantId: string;
  noteId: string | null; // null → mod create
  parentId: string | null; // relevant doar la create
  onSaved: (note: NoteDetail) => void;
  onCancel?: () => void;
  onDeleted?: () => void;
}

export default function NoteEditor({
  tenantId,
  noteId,
  parentId,
  onSaved,
  onCancel,
  onDeleted,
}: Props) {
  const { t } = useTranslation();
  const keyPair = useAuthStore((s) => s.keyPair);
  const verifyPassword = useAuthStore((s) => s.verifyPassword);
  const queryClient = useQueryClient();

  const isEdit = noteId !== null;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugSaved, setSlugSaved] = useState(false);
  const [content, setContent] = useState<string>('');
  const [hydrated, setHydrated] = useState(!isEdit);
  const [error, setError] = useState<string | null>(null);
  // Starea ciornei e declarată aici fiindcă o ating și hidratarea, și salvarea;
  // efectele ei sunt mai jos, în `useNoteDraft`.
  const { autoStatus, setAutoStatus, dirtyRef } = useNoteDraftStatus();
  // View/Edit toggle — default „view" la deschiderea notiței.
  const [mode, setMode] = useState<'view' | 'edit'>(isEdit ? 'view' : 'edit');
  const user = useAuthStore((s) => s.user);

  const { editor, uploadModal, setUploadModal } = useNoteTiptap({
    tenantId,
    noteId,
    isEdit,
    mode,
  });

  const detailQuery = useQuery({
    queryKey: ['note', noteId],
    queryFn: () => notesApi.get(noteId!),
    enabled: !!noteId,
  });

  useEffect(() => {
    if (!isEdit || hydrated || !detailQuery.data || !keyPair || !editor) return;
    const hydrate = async () => {
      try {
        const cred = detailQuery.data;
        const loaded = await loadNoteContent({
          note: cred,
          keyPair,
          userId: user?.id,
          noteId,
        });

        setTitle(loaded.initialTitle);
        setSlug(cred.slug ?? '');
        setContent(loaded.serverHtml); // "server" reference pentru dirty diff
        editor.commands.setContent(loaded.initialHtml, false);
        setHydrated(true);
        if (loaded.restoredDraft) {
          setAutoStatus('draft');
          dirtyRef.current = true;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('notes_ui.editor.decrypt_error'));
      }
    };
    void hydrate();
    // `setAutoStatus` și `dirtyRef` vin din `useNoteDraftStatus` și sunt
    // stabile — lista rămâne cea dinainte de extragere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, hydrated, detailQuery.data, keyPair, editor, user, noteId, t]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!keyPair) throw new Error(t('notes_ui.editor.no_key_reauth'));
      if (!editor) throw new Error(t('notes_ui.editor.not_initialized'));
      return encryptAndSaveNote({
        html: editor.getHTML(),
        title: title.trim(),
        keyPair,
        tenantId,
        noteId,
        parentId: isEdit ? (detailQuery.data?.parent_id ?? null) : parentId,
        userId: user?.id,
      });
    },
    onSuccess: (note) => {
      setContent(editor?.getHTML() ?? '');
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
      if (isEdit) {
        // Punem răspunsul direct în cache: la salvarea făcută la ieșire, o
        // redeschidere rapidă ar hidrata altfel editorul din conținutul vechi.
        queryClient.setQueryData(['note', noteId], note);
        queryClient.invalidateQueries({ queryKey: ['note', noteId] });
        queryClient.invalidateQueries({ queryKey: ['note-versions', noteId] });
        queryClient.invalidateQueries({ queryKey: ['note-versions-count', noteId] });
      }
      dirtyRef.current = false;
      setAutoStatus('saved');
      // Șterge draft-ul local — conținutul e acum pe server.
      if (isEdit && user && noteId) clearDraft(user.id, noteId);
      onSaved(note);
    },
    onError: (err) => {
      setError(errorMessage(err, t('notes_ui.editor.save_error')));
      setAutoStatus('draft');
    },
  });

  // Draft local (keystroke → localStorage) + flush pe server la unmount.
  const { editorTick, scheduleDraftSave, saveDraftExplicit } = useNoteDraft({
    setAutoStatus,
    dirtyRef,
    isEdit,
    hydrated,
    editor,
    user,
    noteId,
    title,
    savedHtml: content,
    savedTitle: detailQuery.data?.title,
    save: () => saveMutation.mutate(),
  });

  const slugMutation = useMutation({
    mutationFn: (newSlug: string) => notesApi.updateSlug(noteId!, newSlug),
    onSuccess: (updated) => {
      setSlug(updated.slug);
      setSlugSaved(true);
      setTimeout(() => setSlugSaved(false), 1500);
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
    },
    onError: (err) => {
      setError(errorMessage(err));
    },
  });

  const noteDelete = useNoteDelete({
    noteId,
    tenantId,
    verifyPassword,
    onDeleted,
    onError: setError,
  });
  const { confirmDelete, setConfirmDelete } = noteDelete;

  const dirty = useMemo(() => {
    if (!isEdit) return title.trim().length > 0 || (editor?.getText() ?? '').length > 0;
    if (!hydrated) return false;
    return editor ? editor.getHTML() !== content || title !== detailQuery.data?.title : false;
    // editorTick forțează re-evaluarea la fiecare transaction chiar dacă
    // referința editor-ului e stabilă (ex: resize imagine consecutiv).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, title, content, isEdit, hydrated, detailQuery.data, editorTick]);

  const loading = isEdit && !hydrated && !error;

  useAssetWrapPropagation({ isEdit, noteId, hydrated, keyPair, tenantId });

  const assetsQuery = useQuery({
    queryKey: ['note-assets-count', noteId],
    queryFn: () => assetsApi.list(noteId!),
    enabled: isEdit && !!noteId,
  });
  const versionsQuery = useQuery({
    queryKey: ['note-versions-count', noteId],
    queryFn: () => notesApi.listVersions(noteId!),
    enabled: isEdit && !!noteId,
  });

  const charCount = editor ? editor.getText().length : 0;
  const wordsCount = editor ? editor.getText().split(/\s+/).filter(Boolean).length : 0;
  const assetsCount = assetsQuery.data?.length ?? 0;
  const versionsCount =
    versionsQuery.data?.length ?? detailQuery.data?.current_version ?? 1;
  const updatedAt = detailQuery.data?.updated_at;
  const updatedLabel = formatRelative(updatedAt);

  return (
    <div className="max-w-none space-y-4">
      {/* ============ Header: titlu + slug + status ============ */}
      <header className="pb-3 border-b border-[var(--border)]">
        <div className="flex items-baseline gap-3">
          <input
            className="flex-1 font-serif text-[28px] font-normal border-0 focus:outline-none focus:ring-0 bg-transparent px-0"
            style={{ letterSpacing: '-0.015em' }}
            placeholder={
              isEdit
                ? t('notes.editor_title_placeholder')
                : parentId
                  ? t('notes.editor_subnote_placeholder')
                  : t('notes.editor_new_placeholder')
            }
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              scheduleDraftSave();
            }}
            disabled={saveMutation.isPending || (isEdit && mode === 'view')}
            readOnly={isEdit && mode === 'view'}
          />
          {isEdit && detailQuery.data?.is_template && (
            <span
              className="badge text-[10px]"
              style={{
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
              }}
              title={t('notes_ui.editor.template_badge_title')}
            >
              {t('notes.template_badge')}
            </span>
          )}
          {isEdit && <AutoSaveIndicator status={autoStatus} />}
        </div>
        {isEdit && (
          <div className="flex items-center gap-2 mt-1.5 text-xs">
            <span className="text-[var(--text-muted)] select-none flex-shrink-0">
              slug:
            </span>
            {mode === 'edit' ? (
              <>
                <input
                  className="flex-1 font-mono text-[var(--text-muted)] bg-transparent border-0 border-b border-dashed border-[var(--border-strong)] focus:outline-none focus:border-brand-500 px-0 py-0.5 w-full"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  onBlur={() => {
                    const trimmed = slug.trim().toLowerCase();
                    if (trimmed && trimmed !== (detailQuery.data?.slug ?? '')) {
                      slugMutation.mutate(trimmed);
                    }
                  }}
                  placeholder={t('notes_ui.editor.slug_placeholder')}
                  pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                  maxLength={64}
                  disabled={slugMutation.isPending}
                />
                {slugMutation.isPending && (
                  <span className="text-[var(--text-dim)] flex-shrink-0">•••</span>
                )}
                {slugSaved && (
                  <span className="text-green-600 dark:text-green-400 flex-shrink-0">{t('notes_ui.editor.slug_saved')}</span>
                )}
              </>
            ) : (
              <span className="flex-1 font-mono text-[var(--text-muted)] truncate">
                {slug || '—'}
              </span>
            )}
          </div>
        )}
      </header>

      {/* ============ Acțiuni primare (sub slug) ============
          User știe deja ce a deschis → butonul principal (Edit / Save /
          Renunță) la vârf ca să-l poată accesa direct, fără scroll. */}
      {!loading && editor && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap -mt-2 mb-3 pb-3"
          style={{
            backgroundImage:
              'linear-gradient(90deg, var(--border) 0%, var(--border) 50%, transparent 100%)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left bottom',
            backgroundSize: '100% 1px',
          }}
        >
          <div className="flex gap-2">
            {mode === 'view' && isEdit ? (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => setMode('edit')}
              >
                {t('common.edit')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-primary text-sm"
                  onClick={() => {
                    setError(null);
                    if (!title.trim()) {
                      setError(t('notes_ui.editor.title_required'));
                      return;
                    }
                    saveMutation.mutate();
                    if (isEdit) setMode('view');
                  }}
                  disabled={saveMutation.isPending || !dirty}
                >
                  {saveMutation.isPending
                    ? t('notes_ui.editor.saving')
                    : isEdit
                      ? t('common.save')
                      : t('common.create')}
                </button>
                {isEdit && mode === 'edit' && (
                  <button
                    type="button"
                    className="btn-outline text-sm"
                    onClick={saveDraftExplicit}
                    disabled={saveMutation.isPending}
                    title={t('notes_ui.editor.save_draft_title')}
                  >
                    {t('notes_ui.editor.save_draft')}
                  </button>
                )}
                {isEdit && mode === 'edit' && (
                  <button
                    type="button"
                    className="btn-outline text-sm"
                    onClick={() => {
                      if (user && noteId) clearDraft(user.id, noteId);
                      setMode('view');
                      setHydrated(false);
                      dirtyRef.current = false;
                      setAutoStatus('idle');
                    }}
                    disabled={saveMutation.isPending}
                  >
                    {t('notes_ui.editor.discard')}
                  </button>
                )}
                {!isEdit && onCancel && (
                  <button
                    type="button"
                    className="btn-outline text-sm"
                    onClick={onCancel}
                    disabled={saveMutation.isPending}
                  >
                    {t('common.cancel')}
                  </button>
                )}
                {isEdit && mode === 'edit' && (
                  <TemplateToggleButton
                    noteId={noteId!}
                    isTemplate={detailQuery.data?.is_template ?? false}
                    ownerId={detailQuery.data?.owner_id ?? ''}
                    currentUserId={user?.id ?? ''}
                    onChanged={() => {
                      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
                      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
                      queryClient.invalidateQueries({ queryKey: ['note-templates', tenantId] });
                    }}
                  />
                )}
                {isEdit && mode === 'edit' && !confirmDelete && (
                  <button
                    type="button"
                    className="btn-outline text-sm inline-flex items-center gap-1.5"
                    style={{
                      color: 'var(--c-rose)',
                      borderColor: 'var(--c-rose)',
                    }}
                    onClick={() => setConfirmDelete(true)}
                    title={t('notes_ui.editor.delete_title')}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                    {t('common.delete')}
                  </button>
                )}
              </>
            )}
          </div>
          {mode === 'edit' && (
            <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
              <kbd className="font-mono bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded px-1.5 py-0.5">/</kbd>
              {t('notes_ui.editor.hint_commands')}
              <span className="text-[var(--text-dim)]">·</span>
              <kbd className="font-mono bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded px-1.5 py-0.5">@</kbd>
              {t('notes_ui.editor.hint_credential')}
              <span className="text-[var(--text-dim)]">·</span>
              <kbd className="font-mono bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded px-1.5 py-0.5">&amp;</kbd>
              {t('notes_ui.editor.hint_member')}
              <span className="text-[var(--text-dim)]">·</span>
              <kbd className="font-mono bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded px-1.5 py-0.5">[[</kbd>
              {t('notes_ui.editor.hint_note_link')}
            </div>
          )}
        </div>
      )}

      {/* ============ Corp editor ============ */}
      {loading && <p className="text-sm text-[var(--text-dim)]">{t('notes.decrypting')}</p>}
      {!loading && editor && (
        <>
          <div
            className={
              mode === 'edit'
                ? 'min-h-[50vh] rounded-md bg-[var(--bg-surface)]/60 ring-1 ring-slate-200 dark:ring-slate-700 transition-colors'
                : ''
            }
          >
            <EditorContent editor={editor} />
            {mode === 'edit' && <TableBubbleMenu editor={editor} />}
            {mode === 'edit' && <ImageBubbleMenu editor={editor} />}
          </div>
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mt-3">
              {error}
            </div>
          )}

          {isEdit && noteId && detailQuery.data && (
            <NoteTagsSection note={detailQuery.data} tenantId={tenantId} />
          )}

          {isEdit && noteId && detailQuery.data && keyPair && (
            <NoteSharesSection
              note={detailQuery.data}
              tenantId={tenantId}
              keyPair={keyPair}
            />
          )}

          {isEdit && noteId && (
            <section className="mt-4 pt-4 border-t border-[var(--border)]">
              <VersionsSection noteId={noteId} tenantId={tenantId} />
            </section>
          )}

          {isEdit && noteId && uploadModal && (
            <AssetUploadModal
              noteId={noteId}
              tenantId={tenantId}
              kind={uploadModal}
              onClose={() => setUploadModal(null)}
              onInsert={(asset) => {
                if (!editor) return;
                // Decide nodul pe baza MIME-ului real al asset-ului, nu a
                // „kind"-ului care a deschis modalul — userul poate reinsera
                // un fișier încărcat anterior ca imagine sau invers.
                const isImage = asset.mime_type.toLowerCase().startsWith('image/');
                const node = isImage
                  ? {
                      type: 'assetImage',
                      attrs: { assetId: asset.id, alt: asset.filename },
                    }
                  : {
                      type: 'fileAttachment',
                      attrs: {
                        assetId: asset.id,
                        filename: asset.filename,
                        mime: asset.mime_type,
                        size: asset.byte_size,
                      },
                    };
                editor
                  .chain()
                  .focus()
                  .insertContent([node, { type: 'paragraph' }])
                  .run();
                scheduleDraftSave();
              }}
            />
          )}

          {isEdit && confirmDelete && <NoteDeleteConfirm {...noteDelete} />}
        </>
      )}

      {/* Statistici — info suplimentar, jos după conținut */}
      {isEdit && hydrated && (
        <section
          className="pt-5 mt-4 border-t border-[var(--border)]"
          aria-label={t('notes_ui.editor.stats_aria')}
        >
          <h3 className="display-title-xs mb-3">{t('notes_ui.editor.stats')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label={t('notes_ui.editor.stat_words')} value={wordsCount} color="cyan" />
            <StatPill label={t('notes_ui.editor.stat_chars')} value={charCount} color="mint" />
            <StatPill label={t('notes_ui.editor.stat_versions')} value={versionsCount} color="purple" />
            <StatPill label={t('notes_ui.editor.stat_attachments')} value={assetsCount} color="amber" />
          </div>
          <div
            className="text-[11px] font-mono mt-3"
            style={{ color: 'var(--text-dim)' }}
          >
            {t('notes_ui.editor.last_modified', { when: updatedLabel })}
          </div>
        </section>
      )}
    </div>
  );
}
